/**
 * ChainOfTheWeek — visual mini relationship graph on the homepage.
 * Real entities + real documented hops from /api/network/chain-of-week.
 * Node → animated connector (dossier-evidence count) → node. The CTA drops
 * the reader into the Connections Explorer with the chain pre-traced.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { GitBranch, ArrowRight, FileText, User, Building2, Landmark, Zap } from "lucide-react";
import { SectionHeader } from "./homeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICON = {
  person: User, people: User,
  organization: Building2, organizations: Building2, agency: Building2,
  government: Landmark, place: Landmark,
  event: Zap, operation: Zap,
};

const Node = ({ n }) => {
  const Icon = KIND_ICON[(n.kind_group || n.kind || "").toLowerCase()] || User;
  return (
    <Link
      to={`/connections/${n.slug}`}
      data-testid={`chain-node-${n.slug}`}
      className="group flex flex-col items-center gap-2 shrink-0 w-[104px] md:w-[120px]"
    >
      <div
        className="w-14 h-14 md:w-16 md:h-16 border overflow-hidden flex items-center justify-center group-hover:border-[#D92D20] transition-colors"
        style={{ borderColor: "#3A3630", background: "#0d0c0a" }}
      >
        {n.portrait_url ? (
          <img src={n.portrait_url} alt={n.name} loading="lazy" className="w-full h-full object-cover" style={{ filter: "saturate(0.7)" }} />
        ) : (
          <Icon size={20} className="text-[#8A8377] group-hover:text-[#D92D20] transition-colors" />
        )}
      </div>
      <div className="text-center">
        <div className="text-[11px] md:text-[12px] leading-tight text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors line-clamp-2">
          {n.name}
        </div>
        {(n.kind || n.kind_group) && (
          <div className="mt-0.5 font-mono text-[7px] uppercase tracking-[0.25em] text-[#7B7168]">
            {n.kind || n.kind_group}
          </div>
        )}
      </div>
    </Link>
  );
};

const Connector = ({ hop }) => (
  <div
    className="flex flex-col items-center shrink-0 pt-6 md:pt-7 w-[52px] md:w-[72px]"
    title={(hop.shared_dossiers || []).map((d) => d.title).join(" · ")}
  >
    <div className="relative w-full h-px overflow-hidden">
      <div className="absolute inset-0 chain-dash" style={{ borderTop: "1px dashed #D92D20AA" }} />
    </div>
    <span className="mt-1.5 flex items-center gap-1 font-mono text-[8px] text-[#A68F5E] uppercase tracking-[0.1em]">
      <FileText size={9} /> {(hop.shared_dossiers || []).length}
    </span>
  </div>
);

const ChainOfTheWeek = () => {
  const [chain, setChain] = useState(null);

  useEffect(() => {
    let dead = false;
    axios.get(`${API}/network/chain-of-week`, { timeout: 20000 })
      .then((r) => { if (!dead) setChain(r.data); })
      .catch(() => { /* section gracefully degrades */ });
    return () => { dead = true; };
  }, []);

  if (!chain) return null;
  const first = chain.nodes[0], last = chain.nodes[chain.nodes.length - 1];

  return (
    <section className="border-t" style={{ borderColor: "#2D2A26", background: "#08070A" }} data-testid="chain-of-week">
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .chain-dash { animation: chaindash 1.6s linear infinite; }
          @keyframes chaindash { from { transform: translateX(-8px); } to { transform: translateX(0); } }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-20">
        <SectionHeader
          file={`File 010 · Evidence Network · ${chain.week}`}
          title="Chain of the Week"
          subtitle="The Archive is not a collection of articles — it is an interconnected evidence network. Every hop below is backed by published dossiers."
        />
        <div className="border p-5 md:p-8 relative overflow-hidden" style={{ borderColor: "#3A3630", background: "#12110e" }}>
          <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20]">
            <GitBranch size={12} />
            {first.name} → {last.name}
          </div>
          <p className="font-mono text-[10px] text-[#757065] mt-1.5 uppercase tracking-[0.2em]" data-testid="chain-of-week-degrees">
            {chain.degrees} documented degrees of separation
          </p>

          <div
            className="mt-5 md:mt-6 overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0 pb-2 snap-x relative"
            style={{ scrollPaddingLeft: "1.25rem", WebkitOverflowScrolling: "touch" }}
            data-testid="chain-of-week-nodes"
          >
            <div className="flex items-start min-w-max">
              {chain.nodes.map((n, i) => (
                <React.Fragment key={n.slug}>
                  <div className="snap-start"><Node n={n} /></div>
                  {i < chain.hops.length && <Connector hop={chain.hops[i]} />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="md:hidden font-mono text-[8px] uppercase tracking-[0.3em] text-[#454038]">
            Swipe to trace the chain →
          </div>

          <Link
            to={`/connections?from=${chain.from}&to=${chain.to}`}
            className="mt-5 md:mt-6 inline-flex items-center gap-2 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20] border-[#D92D20] hover:bg-[#D92D20] hover:text-[#0a0907] transition-colors"
            data-testid="chain-of-week-cta"
          >
            View full chain <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ChainOfTheWeek;
